import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, ArrowLeft, Tag, Share2, Bookmark, ArrowRight, Check } from 'lucide-react';
import postsContent from './blogPostsContent';

const BOOKMARKS_KEY = '@mybank:blog-bookmarks';

const getBookmarks = () => {
  try {
    return JSON.parse(localStorage.getItem(BOOKMARKS_KEY) || '[]');
  } catch {
    return [];
  }
};

const bolaDeNevePost = {
  category: 'Educação Financeira',
  title: 'Método bola de neve: como quitar dívidas mais rápido',
  excerpt: 'Entenda a estratégia que ajudou milhares de pessoas a sair das dívidas pagando as menores primeiro.',
  readTime: '7 min',
  date: '28 de fevereiro de 2025',
  author: 'Diego Alves',
  authorRole: 'Head de IA — MyBank',
  authorBg: 'bg-emerald-600',
  authorAvatar: 'DA',
  authorBio: 'Mestre em Ciência de Dados pela UNICAMP. Especialista em modelos preditivos para comportamento financeiro.',
  tags: ['Dívidas', 'Planejamento', 'Método Bola de Neve', 'Finanças Pessoais'],
  sections: [
    {
      type: 'p',
      content: 'O método bola de neve é uma das estratégias mais populares para quitar dívidas — e há uma boa razão para isso: ele usa a psicologia a seu favor, criando vitórias rápidas que mantêm a motivação em alta.',
    },
    { type: 'h2', content: 'Como funciona' },
    { type: 'p', content: 'A ideia é simples: liste todas as suas dívidas da menor para a maior (independente da taxa de juros), pague o mínimo em todas exceto na menor, e concentre todo o dinheiro disponível nela. Quando quitar a menor, o valor que você pagava nela passa para a próxima.' },
    { type: 'p', content: 'O nome "bola de neve" vem exatamente desse efeito: cada dívida quitada libera mais dinheiro para atacar a próxima — a bola cresce conforme rola.' },
    { type: 'h2', content: 'Por que funciona na prática?' },
    { type: 'p', content: 'Matematicamente, faz mais sentido pagar as dívidas com maior taxa de juros primeiro (estratégia "avalanche"). Mas estudos comportamentais mostram que a maioria das pessoas abandona o plano por falta de progresso visível. O método bola de neve cria vitórias rápidas que sustentam o compromisso.' },
    { type: 'quote', content: 'A única dívida que importa é a que você ainda não pagou.' },
    { type: 'h2', content: 'Exemplo prático' },
    { type: 'p', content: 'Imagine que você tem: Cartão A de R$ 800 (9%/mês), Empréstimo de R$ 5.000 (3%/mês) e Cartão B de R$ 12.000 (8%/mês). Com R$ 600 disponíveis por mês, você concentra tudo no Cartão A nos primeiros meses. Quitado ele, todo aquele valor vai para o empréstimo. Em aproximadamente 14 meses, você pode estar completamente livre.' },
    { type: 'h2', content: 'Como o MyBank automatiza isso' },
    { type: 'p', content: 'O MyBank identifica automaticamente suas dívidas, calcula o plano ótimo de pagamento baseado no método bola de neve e te avisa quando liberar recurso para atacar a próxima. Você só precisa seguir o plano — nós fazemos o trabalho pesado.' },
  ],
};

const allPosts = {
  'metodo-bola-de-neve-dividas': bolaDeNevePost,
  ...postsContent,
};

const getPost = (slug) => allPosts[slug] ?? bolaDeNevePost;


const renderSection = (section, i) => {
  switch (section.type) {
    case 'h2':
      return <h2 key={i} className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">{section.content}</h2>;
    case 'quote':
      return (
        <blockquote key={i} className="border-l-4 border-primary-500 pl-5 py-2 my-6 bg-primary-50 dark:bg-primary-900/10 rounded-r-xl">
          <p className="text-gray-700 dark:text-gray-300 italic text-lg">{section.content}</p>
        </blockquote>
      );
    default:
      return <p key={i} className="text-gray-700 dark:text-gray-300 leading-relaxed my-4 text-lg">{section.content}</p>;
  }
};

const relatedPostsMap = {
  'metodo-bola-de-neve-dividas': [
    { slug: 'fundo-emergencia-quanto-guardar', title: 'Fundo de emergência: quanto você realmente precisa guardar?', readTime: '8 min', image: '🛡️' },
    { slug: 'gastos-invisiveis', title: 'Os gastos invisíveis que estão consumindo seu dinheiro', readTime: '6 min', image: '🔍' },
    { slug: 'cartao-credito-vilao-ou-aliado', title: 'Cartão de crédito: vilão ou aliado?', readTime: '7 min', image: '💳' },
  ],
  'regra-50-30-20-funciona': [
    { slug: 'metas-financeiras-smart', title: 'Como definir metas financeiras que você vai realmente cumprir', readTime: '5 min', image: '🎯' },
    { slug: 'gastos-invisiveis', title: 'Os gastos invisíveis que estão consumindo seu dinheiro', readTime: '6 min', image: '🔍' },
    { slug: 'fundo-emergencia-quanto-guardar', title: 'Fundo de emergência: quanto você realmente precisa guardar?', readTime: '8 min', image: '🛡️' },
  ],
  'tesouro-direto-iniciantes': [
    { slug: 'ia-financas-pessoais', title: 'Como a IA está transformando as finanças pessoais', readTime: '10 min', image: '🤖' },
    { slug: 'fundo-emergencia-quanto-guardar', title: 'Fundo de emergência: quanto você realmente precisa guardar?', readTime: '8 min', image: '🛡️' },
    { slug: 'metas-financeiras-smart', title: 'Como definir metas financeiras que você vai realmente cumprir', readTime: '5 min', image: '🎯' },
  ],
  'gastos-invisiveis': [
    { slug: 'regra-50-30-20-funciona', title: 'A regra 50-30-20 ainda funciona em 2025?', readTime: '9 min', image: '📊' },
    { slug: 'cartao-credito-vilao-ou-aliado', title: 'Cartão de crédito: vilão ou aliado?', readTime: '7 min', image: '💳' },
    { slug: 'metas-financeiras-smart', title: 'Como definir metas financeiras que você vai realmente cumprir', readTime: '5 min', image: '🎯' },
  ],
  'fundo-emergencia-quanto-guardar': [
    { slug: 'tesouro-direto-iniciantes', title: 'Tesouro Direto em 2025: guia completo para iniciantes', readTime: '12 min', image: '📈' },
    { slug: 'metodo-bola-de-neve-dividas', title: 'Método bola de neve: como quitar dívidas mais rápido', readTime: '7 min', image: '💸' },
    { slug: 'metas-financeiras-smart', title: 'Como definir metas financeiras que você vai realmente cumprir', readTime: '5 min', image: '🎯' },
  ],
  'ia-financas-pessoais': [
    { slug: 'tesouro-direto-iniciantes', title: 'Tesouro Direto em 2025: guia completo para iniciantes', readTime: '12 min', image: '📈' },
    { slug: 'gastos-invisiveis', title: 'Os gastos invisíveis que estão consumindo seu dinheiro', readTime: '6 min', image: '🔍' },
    { slug: 'cartao-credito-vilao-ou-aliado', title: 'Cartão de crédito: vilão ou aliado?', readTime: '7 min', image: '💳' },
  ],
  'cartao-credito-vilao-ou-aliado': [
    { slug: 'metodo-bola-de-neve-dividas', title: 'Método bola de neve: como quitar dívidas mais rápido', readTime: '7 min', image: '💸' },
    { slug: 'gastos-invisiveis', title: 'Os gastos invisíveis que estão consumindo seu dinheiro', readTime: '6 min', image: '🔍' },
    { slug: 'regra-50-30-20-funciona', title: 'A regra 50-30-20 ainda funciona em 2025?', readTime: '9 min', image: '📊' },
  ],
  'metas-financeiras-smart': [
    { slug: 'fundo-emergencia-quanto-guardar', title: 'Fundo de emergência: quanto você realmente precisa guardar?', readTime: '8 min', image: '🛡️' },
    { slug: 'regra-50-30-20-funciona', title: 'A regra 50-30-20 ainda funciona em 2025?', readTime: '9 min', image: '📊' },
    { slug: 'ia-financas-pessoais', title: 'Como a IA está transformando as finanças pessoais', readTime: '10 min', image: '🤖' },
  ],
};

const defaultRelated = [
  { slug: 'fundo-emergencia-quanto-guardar', title: 'Fundo de emergência: quanto você realmente precisa guardar?', readTime: '8 min', image: '🛡️' },
  { slug: 'gastos-invisiveis', title: 'Os gastos invisíveis que estão consumindo seu dinheiro', readTime: '6 min', image: '🔍' },
  { slug: 'cartao-credito-vilao-ou-aliado', title: 'Cartão de crédito: vilão ou aliado?', readTime: '7 min', image: '💳' },
];

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  const related = relatedPostsMap[slug] ?? defaultRelated;

  const [bookmarked, setBookmarked] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null); // null | 'copied' | 'shared'

  useEffect(() => {
    setBookmarked(getBookmarks().includes(slug));
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, url });
        setShareFeedback('shared');
      } catch {
        // usuario cancelou
        return;
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const el = document.createElement('textarea');
        el.value = url;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setShareFeedback('copied');
    }
    setTimeout(() => setShareFeedback(null), 2500);
  };

  const handleBookmark = () => {
    const bookmarks = getBookmarks();
    const isBookmarked = bookmarks.includes(slug);
    const updated = isBookmarked
      ? bookmarks.filter((s) => s !== slug)
      : [...bookmarks, slug];
    localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(updated));
    setBookmarked(!isBookmarked);
  };

  return (
    <div className="bg-white dark:bg-gray-950 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-b from-gray-950 to-gray-900 pt-12 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 text-sm">
            <ArrowLeft size={16} />
            Voltar ao Blog
          </Link>

          <div className="flex items-center gap-3 mb-5">
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-primary-600/20 text-primary-400 border border-primary-500/30">
              {post.category}
            </span>
            <span className="text-gray-500 text-sm flex items-center gap-1">
              <Clock size={12} /> {post.readTime} de leitura
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
            {post.title}
          </h1>

          <p className="text-xl text-gray-400 mb-8 leading-relaxed">{post.excerpt}</p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full ${post.authorBg} flex items-center justify-center text-white font-bold text-sm`}>
                {post.authorAvatar}
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{post.author}</p>
                <p className="text-gray-400 text-xs">{post.authorRole} · {post.date}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={handleShare}
                  title="Compartilhar"
                  className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                >
                  {shareFeedback ? <Check size={18} className="text-green-400" /> : <Share2 size={18} />}
                </button>
                {shareFeedback === 'copied' && (
                  <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-gray-800 text-white px-2 py-1 rounded-lg">
                    Link copiado!
                  </span>
                )}
              </div>
              <button
                onClick={handleBookmark}
                title={bookmarked ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                className={`p-2 rounded-lg transition-colors ${
                  bookmarked
                    ? 'bg-primary-600/20 text-primary-400 hover:bg-primary-600/30'
                    : 'hover:bg-gray-800 text-gray-400 hover:text-white'
                }`}
              >
                <Bookmark size={18} className={bookmarked ? 'fill-current' : ''} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Content */}
        <div className="mb-10">
          {post.sections.map((section, i) => renderSection(section, i))}
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 pt-8 border-t border-gray-200 dark:border-gray-800 flex-wrap">
          <Tag size={14} className="text-gray-400" />
          {post.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-medium">
              {tag}
            </span>
          ))}
        </div>

        {/* Author bio */}
        <div className="mt-10 p-6 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 flex gap-4">
          <div className={`w-14 h-14 rounded-full ${post.authorBg} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
            {post.authorAvatar}
          </div>
          <div>
            <p className="font-bold text-gray-900 dark:text-white">{post.author}</p>
            <p className="text-primary-600 text-sm font-medium mb-2">{post.authorRole}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{post.authorBio}</p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 p-8 rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 text-white text-center">
          <p className="text-sm font-semibold opacity-80 mb-2">Gostou do conteúdo?</p>
          <h3 className="text-2xl font-bold mb-3">Coloque em prática com o MyBank</h3>
          <p className="text-white/80 mb-6 max-w-md mx-auto text-sm">
            Crie sua conta gratuita e comece a aplicar o método bola de neve agora — com acompanhamento de IA e alertas automáticos.
          </p>
          <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors">
            Criar conta grátis <ArrowRight size={16} />
          </Link>
        </div>

        {/* Related posts */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Continue lendo</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-primary-400 dark:hover:border-primary-600 transition-all bg-white dark:bg-gray-900">
                <div className="h-24 bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-4xl">
                  {p.image}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm group-hover:text-primary-600 transition-colors leading-snug mb-2">
                    {p.title}
                  </h3>
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock size={11} /> {p.readTime}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
